/**
 * PresentationDock — Horizontaler Bilder-Dock unten rechts.
 *
 * Persistenz: Bilder werden zentral in der Datenbank gespeichert
 * (user-scoped, projekt-übergreifend) via:
 *   GET    /api/presentations         → alle Slides des Users
 *   POST   /api/presentations         → Bild hochladen (FormData)
 *   PATCH  /api/presentations/reorder  → Reihenfolge ändern
 *   DELETE /api/presentations/:id      → Slide löschen
 *
 * Fallback: IndexedDB wenn API nicht erreichbar.
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
// import { apiFetch, API_URL } from '../../lib/apiBase'  // TODO: enable when API endpoints exist

// ─── Types ──────────────────────────────────────────────────────────────────

export interface PresentationSlide {
  id: string
  imageUrl: string       // Server-URL oder Object-URL (Fallback)
  thumbnailUrl: string   // Server-URL oder Data-URL (Fallback)
  sortOrder: number
  filename?: string
}

interface DockProps {
  projectId: string | null
}

// ─── IndexedDB Fallback ─────────────────────────────────────────────────────

const DB_NAME = 'orto_presentation'
const DB_VERSION = 1
const STORE_NAME = 'slides'

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE_NAME)) {
        req.result.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function idbSaveSlide(slide: PresentationSlide, file: File): Promise<void> {
  const db = await openDB()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  tx.objectStore(STORE_NAME).put({
    id: slide.id,
    imageBlob: file,
    thumbnailUrl: slide.thumbnailUrl,
    sortOrder: slide.sortOrder,
    filename: slide.filename,
  })
  await new Promise<void>((res, rej) => { tx.oncomplete = () => res(); tx.onerror = () => rej(tx.error) })
  db.close()
}

async function idbLoadAll(): Promise<PresentationSlide[]> {
  try {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readonly')
    const all: any[] = await new Promise((res, rej) => {
      const r = tx.objectStore(STORE_NAME).getAll()
      r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error)
    })
    db.close()
    return all
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map(r => ({
        id: r.id,
        imageUrl: r.imageBlob instanceof Blob ? URL.createObjectURL(r.imageBlob) : '',
        thumbnailUrl: r.thumbnailUrl || '',
        sortOrder: r.sortOrder ?? 0,
        filename: r.filename,
      }))
  } catch { return [] }
}

async function idbDeleteSlide(id: string): Promise<void> {
  try {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).delete(id)
    await new Promise<void>((res) => { tx.oncomplete = () => res() })
    db.close()
  } catch { /* ignore */ }
}

async function idbSaveOrder(slides: PresentationSlide[]): Promise<void> {
  try {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    for (const s of slides) {
      const req = store.get(s.id)
      req.onsuccess = () => {
        if (req.result) {
          req.result.sortOrder = s.sortOrder
          store.put(req.result)
        }
      }
    }
    await new Promise<void>((res) => { tx.oncomplete = () => res() })
    db.close()
  } catch { /* ignore */ }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

/** Resize image to thumbnail Data URL */
function createThumbnail(src: string, maxW = 120, maxH = 80): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const scale = Math.min(maxW / img.width, maxH / img.height, 1)
      const w = Math.round(img.width * scale)
      const h = Math.round(img.height * scale)
      const c = document.createElement('canvas')
      c.width = w; c.height = h
      c.getContext('2d')!.drawImage(img, 0, 0, w, h)
      resolve(c.toDataURL('image/jpeg', 0.7))
    }
    img.src = src
  })
}

// ─── Component ──────────────────────────────────────────────────────────────

export function PresentationDock({ projectId }: DockProps) {
  const [slides, setSlides] = useState<PresentationSlide[]>([])
  const [activeSlideId, setActiveSlideId] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [dropIdx, setDropIdx] = useState<number | null>(null)
  const [visible, setVisible] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [overflowDir, setOverflowDir] = useState<'right' | 'left' | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const dockRef = useRef<HTMLDivElement>(null)

  // ── Shift+Space toggle ─────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.shiftKey && e.code === 'Space' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Don't toggle when typing in an input/textarea
        const tag = (e.target as HTMLElement)?.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return
        e.preventDefault()
        setVisible(v => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // ── Dynamic max-width: dock must not overlap dock-pill ──────────────────
  const [maxW, setMaxW] = useState<number | null>(null)

  const calcMaxWidth = useCallback(() => {
    const dockPill = document.getElementById('dock-pill')
    if (!dockPill) { setMaxW(null); return }
    const pillRect = dockPill.getBoundingClientRect()
    const available = window.innerWidth - pillRect.right - 48 - 16 // 48px gap to pill + 16px right margin
    setMaxW(Math.max(80, available)) // minimum 80px (plus-button only)
  }, [])

  useEffect(() => {
    calcMaxWidth()
    window.addEventListener('resize', calcMaxWidth)
    return () => window.removeEventListener('resize', calcMaxWidth)
  }, [calcMaxWidth])

  // ── Check scroll overflow direction ────────────────────────────────────
  const checkOverflow = useCallback(() => {
    const el = scrollRef.current
    if (!el) { setOverflowDir(null); return }
    const hasContentOverflow = el.scrollWidth > el.clientWidth + 2
    if (!hasContentOverflow) { setOverflowDir(null); return }
    const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 2
    const atStart = el.scrollLeft <= 2
    if (atEnd && !atStart) setOverflowDir('left')
    else setOverflowDir('right')
  }, [])

  useEffect(() => {
    checkOverflow()
    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [checkOverflow, slides.length, maxW])

  const handleArrowClick = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    if (overflowDir === 'right') {
      el.scrollBy({ left: 120, behavior: 'smooth' })
    } else {
      el.scrollBy({ left: -120, behavior: 'smooth' })
    }
    setTimeout(checkOverflow, 350)
  }, [overflowDir, checkOverflow])

  // ── Load slides from IndexedDB (always reliable) ───────────────────────
  useEffect(() => {
    let cancelled = false
    idbLoadAll().then(stored => {
      if (!cancelled) setSlides(stored)
    })
    return () => { cancelled = true }
  }, [])

  // ── Add images ─────────────────────────────────────────────────────────
  const addImages = useCallback(async (files: FileList | File[]) => {
    // 1. Collect all valid image files
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'))
    if (imageFiles.length === 0) return

    // 2. Create all slides in parallel
    const newSlides = await Promise.all(
      imageFiles.map(async (file, i) => {
        const objectUrl = URL.createObjectURL(file)
        const thumbUrl = await createThumbnail(objectUrl)
        return {
          slide: {
            id: generateId(),
            imageUrl: objectUrl,
            thumbnailUrl: thumbUrl,
            sortOrder: i, // adjusted below
            filename: file.name,
          } as PresentationSlide,
          file,
        }
      })
    )

    // 3. Add all at once (sortOrder relative to existing count)
    setSlides(prev => {
      const base = prev.length
      return [
        ...prev,
        ...newSlides.map(({ slide }, i) => ({ ...slide, sortOrder: base + i })),
      ]
    })

    // 4. Persist each to IndexedDB + try API
    for (const { slide, file } of newSlides) {
      // Always save to IndexedDB
      idbSaveSlide(slide, file)

      // TODO: API sync when backend endpoint exists
      // const formData = new FormData()
      // formData.append('file', file)
      // formData.append('sortOrder', String(slide.sortOrder))
      // apiFetch(`${API_URL}/api/presentations`, { method: 'POST', body: formData })
    }
  }, [])

  // ── External drag & drop ───────────────────────────────────────────────
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation()
    if (e.dataTransfer.types.includes('Files')) setDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation()
    setDragOver(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation()
    setDragOver(false)
    if (e.dataTransfer.files.length > 0) addImages(e.dataTransfer.files)
  }, [addImages])

  // ── Internal reorder drag ──────────────────────────────────────────────
  const handleThumbDragStart = useCallback((e: React.DragEvent, idx: number) => {
    e.dataTransfer.effectAllowed = 'move'
    setDragIdx(idx)
  }, [])

  const handleThumbDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDropIdx(idx)
  }, [])

  const handleThumbDrop = useCallback((e: React.DragEvent, targetIdx: number) => {
    e.preventDefault(); e.stopPropagation()
    if (dragIdx === null || dragIdx === targetIdx) {
      setDragIdx(null); setDropIdx(null)
      return
    }
    setSlides(prev => {
      const next = [...prev]
      const [moved] = next.splice(dragIdx, 1)
      next.splice(targetIdx, 0, moved)
      const updated = next.map((s, i) => ({ ...s, sortOrder: i }))

      // Persist order
      idbSaveOrder(updated)

      return updated
    })
    setDragIdx(null); setDropIdx(null)
  }, [dragIdx])

  const handleThumbDragEnd = useCallback(() => {
    setDragIdx(null); setDropIdx(null)
  }, [])

  // ── Delete slide ───────────────────────────────────────────────────────
  const removeSlide = useCallback((id: string) => {
    setSlides(prev => prev.filter(s => s.id !== id))
    if (activeSlideId === id) setActiveSlideId(null)

    idbDeleteSlide(id)
  }, [activeSlideId])

  // ── Slider navigation ───────────────────────────────────────────────────
  const toggleSlide = useCallback((id: string) => {
    setActiveSlideId(prev => prev === id ? null : id)
  }, [])

  const activeIdx = activeSlideId ? slides.findIndex(s => s.id === activeSlideId) : -1
  const activeSlide = activeIdx >= 0 ? slides[activeIdx] : null

  const goPrev = useCallback(() => {
    if (slides.length === 0) return
    setActiveSlideId(slides[activeIdx <= 0 ? slides.length - 1 : activeIdx - 1].id)
  }, [slides, activeIdx])

  const goNext = useCallback(() => {
    if (slides.length === 0) return
    setActiveSlideId(slides[activeIdx >= slides.length - 1 ? 0 : activeIdx + 1].id)
  }, [slides, activeIdx])

  // Keyboard: Left/Right to navigate, Escape to close
  useEffect(() => {
    if (!activeSlide) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev() }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goNext() }
      else if (e.key === 'Escape') setActiveSlideId(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeSlide, goPrev, goNext])

  // ── Render ─────────────────────────────────────────────────────────────
  return createPortal(
    <>
      {activeSlide && (
        <div className="pres-slider" onClick={() => setActiveSlideId(null)}>
          <div className="pres-slider__stage" onClick={e => e.stopPropagation()}>
            <img
              className="pres-slider__img"
              src={activeSlide.imageUrl}
              alt={activeSlide.filename || ''}
              draggable={false}
            />
          </div>

          {slides.length > 1 && (
            <>
              <button className="pres-slider__arrow pres-slider__arrow--prev" onClick={e => { e.stopPropagation(); goPrev() }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button className="pres-slider__arrow pres-slider__arrow--next" onClick={e => { e.stopPropagation(); goNext() }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 6 15 12 9 18" />
                </svg>
              </button>
            </>
          )}

        </div>
      )}

      <div
        ref={dockRef}
        className={`pres-dock${dragOver ? ' pres-dock--drag-over' : ''}${!visible ? ' pres-dock--hidden' : ''}`}
        style={maxW != null ? { maxWidth: maxW } : undefined}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        data-component="PresentationDock"
      >
        <button className="pres-dock__add" title="Bild hinzufügen" onClick={() => fileInputRef.current?.click()}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => { if (e.target.files) addImages(e.target.files); e.target.value = '' }}
        />

        <div className="pres-dock__scroll" ref={scrollRef} onScroll={checkOverflow}>
          {slides.map((slide, i) => (
            <div
              key={slide.id}
              className={`pres-dock__thumb-wrap${activeSlideId === slide.id ? ' is-active' : ''}${dropIdx === i && dragIdx !== null && dragIdx !== i ? ' pres-dock__thumb-wrap--drop-target' : ''}`}
              draggable
              onDragStart={(e) => handleThumbDragStart(e, i)}
              onDragOver={(e) => handleThumbDragOver(e, i)}
              onDrop={(e) => handleThumbDrop(e, i)}
              onDragEnd={handleThumbDragEnd}
              onClick={() => toggleSlide(slide.id)}
            >
              <img className="pres-dock__thumb" src={slide.thumbnailUrl} alt={slide.filename || ''} draggable={false} />
              <button
                className="pres-dock__remove"
                title="Entfernen"
                onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(slide.id) }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
        </div>

        {overflowDir && (
          <button className="pres-dock__more" title={overflowDir === 'right' ? 'Mehr anzeigen' : 'Zurück'} onClick={handleArrowClick}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {overflowDir === 'right'
                ? <polyline points="9 6 15 12 9 18" />
                : <polyline points="15 18 9 12 15 6" />
              }
            </svg>
          </button>
        )}

        {/* Confirm delete popover */}
        {confirmDeleteId && (
          <div className="pres-dock__confirm">
            <span className="pres-dock__confirm-text">Entfernen?</span>
            <button
              className="pres-dock__confirm-yes"
              onClick={(e) => { e.stopPropagation(); removeSlide(confirmDeleteId); setConfirmDeleteId(null) }}
            >Ja</button>
            <button
              className="pres-dock__confirm-no"
              onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null) }}
            >Nein</button>
          </div>
        )}
      </div>
    </>,
    document.body
  )
}
